import { useParams, NavLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览' },
    ...(currentCategory ? [{ label: currentCategory.name }] : []),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 草莓色研社` : '分类浏览 - 草莓色研社',
    description: currentCategory?.description || '浏览草莓视频调色相关的全部内容分类',
    url: `https://strawberrycolor.cn/category/${activeCategory}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mb-10">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory ? currentCategory.name : '分类浏览'}
                </h1>
                {currentCategory && (
                  <p className="text-foreground-500 mt-2 max-w-2xl">
                    {currentCategory.description}
                  </p>
                )}
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-10 flex-wrap">
                {categories.map((cat) => (
                  <NavLink
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => {
                      setActiveCategory(cat.slug);
                      window.scrollTo({ top: 0, behavior: 'instant' as const });
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50 border-primary-500'
                        : 'bg-background-50 text-foreground-600 border-background-200/70 hover:border-primary-200/60'
                    }`}
                  >
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </NavLink>
                ))}
              </div>

              {/* Articles Grid */}
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <article
                      key={article.slug}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300"
                    >
                      <NavLink to={`/article/${article.slug}`} className="block">
                        <div className="relative overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute top-3 left-3">
                            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-background-50">
                              {currentCategory?.name}
                            </span>
                          </div>
                        </div>
                        <div className="p-5">
                          <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-foreground-400">
                            <span className="flex items-center gap-1">
                              <span className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-user-line"></i>
                              </span>
                              {article.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-time-line"></i>
                              </span>
                              {article.readTime}
                            </span>
                          </div>
                        </div>
                      </NavLink>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章，请选择其他分类查看</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}